import { useState } from 'react';
import { DialogConfirmar } from '../../../components/admin/dialog-confirmar';
import { SnackbarFeedback, useFeedback } from '../../../components/admin/feedback';
import { useSelector } from 'react-redux';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import type { RootState } from '../../../store';
import {
  useAdminAtualizarCupomMutation,
  useAdminCriarCupomMutation,
  useAdminListarCuponsQuery,
  useAdminListarRedesQuery,
  useAdminMeQuery,
  useAdminRemoverCupomMutation,
} from '../../../api';
import { HeaderTela } from '../../../components/admin/header-tela';
import { PaginacaoTabela, useUltimaPagina } from '../../../components/admin/paginacao-tabela';
import { ehCupomDeFrete } from '../../../lib/frete-gratis'

interface CupomLinha {
  id: number;
  codigo: string;
  descricao: string | null;
  tipoDesconto: 'PERCENTUAL' | 'VALOR_FIXO' | 'FRETE_GRATIS' | 'FRETE_PERCENTUAL' | 'FRETE_VALOR'
  valorDesconto: string;
  valorMinimoPedido: string;
  usosAtuais: number;
  limiteTotalUsos: number | null;
  usoUnicoPorCliente: boolean;
  escopo: 'LOJA' | 'REDE';
  idRedeCardapio: number | null;
  limiteUsosPorLoja: number | null;
  ativo: boolean;
  dataInicio: string | null;
  dataFim: string | null;
  /** Cupom de rede de outra loja: aparece aqui, mas quem edita é a matriz. */
  somenteLeitura: boolean;
}

interface FormCupom {
  codigo: string;
  descricao: string;
  tipo: CupomLinha['tipoDesconto']
  valor: string;
  valorMinimo: string;
  limite: string;
  usoUnico: boolean;
  escopo: 'LOJA' | 'REDE';
  idRede: number | '';
  limitePorLoja: string;
  dataInicio: string;
  dataFim: string;
  ativo: boolean;
}

const FORM_VAZIO: FormCupom = {
  codigo: '',
  descricao: '',
  tipo: 'PERCENTUAL',
  valor: '',
  valorMinimo: '',
  limite: '',
  usoUnico: false,
  escopo: 'LOJA',
  idRede: '',
  limitePorLoja: '',
  dataInicio: '',
  dataFim: '',
  ativo: true,
};

const ROTULO_DO_TIPO: Record<CupomLinha['tipoDesconto'], string> = {
  PERCENTUAL: '%',
  VALOR_FIXO: 'R$',
  FRETE_GRATIS: 'Frete grátis',
  FRETE_PERCENTUAL: 'Frete %',
  FRETE_VALOR: 'Frete R$',
}

/** ISO (UTC) → valor do `<input type="datetime-local">`, no fuso do navegador. */
function paraInputLocal(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return new Date(d.getTime() - d.getTimezoneOffset() * 60_000).toISOString().slice(0, 16);
}

/** Valor do input → ISO, ou `null` para LIMPAR a data (undefined só seria ignorado). */
function deInputLocal(valor: string): string | null {
  return valor ? new Date(valor).toISOString() : null;
}

function adornoDoValor(tipo: FormCupom['tipo']): { startAdornment?: JSX.Element; endAdornment?: JSX.Element } | undefined {
  if (tipo === 'FRETE_VALOR') return { startAdornment: <InputAdornment position="start">R$</InputAdornment> }
  if (tipo === 'FRETE_GRATIS' || tipo === 'FRETE_PERCENTUAL') return { endAdornment: <InputAdornment position="end">%</InputAdornment> }
  return undefined
}

function formDoCupom(c: CupomLinha): FormCupom {
  return {
    codigo: c.codigo,
    descricao: c.descricao ?? '',
    tipo: c.tipoDesconto,
    valor: c.tipoDesconto === 'FRETE_GRATIS' ? '' : String(Number(c.valorDesconto)),
    valorMinimo: Number(c.valorMinimoPedido) > 0 ? String(Number(c.valorMinimoPedido)) : '',
    limite: c.limiteTotalUsos != null ? String(c.limiteTotalUsos) : '',
    usoUnico: c.usoUnicoPorCliente,
    escopo: c.escopo,
    idRede: c.idRedeCardapio ?? '',
    limitePorLoja: c.limiteUsosPorLoja != null ? String(c.limiteUsosPorLoja) : '',
    dataInicio: paraInputLocal(c.dataInicio),
    dataFim: paraInputLocal(c.dataFim),
    ativo: c.ativo,
  };
}

export function AdminCupomPage(): JSX.Element {
  const idConfig = useSelector((s: RootState) => s.adminAuth.idConfiguracaoSelecionada)!;
  const [pagina, setPagina] = useState(0);
  const [tamanhoPagina, setTamanhoPagina] = useState(20);
  const { data, isLoading } = useAdminListarCuponsQuery({ idConfig, pagina: pagina + 1, tamanhoPagina });
  const { itens, total } = useUltimaPagina(data);
  const cupons = itens as CupomLinha[];
  const [criar, statusCriar] = useAdminCriarCupomMutation();
  const [atualizar, statusAtualizar] = useAdminAtualizarCupomMutation();
  const [remover] = useAdminRemoverCupomMutation();
  /** Cupom aguardando confirmação de inativação — `null` = nenhum. */
  const [inativando, setInativando] = useState<{ id: number; codigo: string } | null>(null);

  // Gating: empresa que não é franquia não vê NADA de rede — nem a coluna, nem
  // os campos do formulário. Ver docs/specs/cupom-rede.md §7.
  const { data: me } = useAdminMeQuery();
  const podeRede = me?.redeGerenciavel ?? false;
  const veRede = me?.redeVisivel ?? false;
  const { data: redes } = useAdminListarRedesQuery(undefined, { skip: !podeRede });

  /** `null` = fechado; `0` = criando; `>0` = editando aquele cupom. */
  const [editandoId, setEditandoId] = useState<number | null>(null);
  const [form, setForm] = useState<FormCupom>(FORM_VAZIO);
  const [erro, setErro] = useState<string | null>(null);
  const { feedback, mostrarSucesso, mostrarErro, fecharFeedback } = useFeedback();

  const salvando = statusCriar.isLoading || statusAtualizar.isLoading;
  const ehEdicao = editandoId != null && editandoId > 0;

  function set<K extends keyof FormCupom>(campo: K, valor: FormCupom[K]): void {
    setForm((f) => ({ ...f, [campo]: valor }));
  }

  function escolherTipo(tipo: FormCupom['tipo']): void {
    setForm((f) => ({ ...f, tipo, valor: f.tipo !== tipo && (ehCupomDeFrete(f.tipo) || ehCupomDeFrete(tipo)) ? '' : f.valor }))
  }

  function abrirNovo(): void {
    setErro(null);
    setForm(FORM_VAZIO);
    setEditandoId(0);
  }

  function abrirEdicao(c: CupomLinha): void {
    setErro(null);
    setForm(formDoCupom(c));
    setEditandoId(c.id);
  }

  async function salvar(): Promise<void> {
    setErro(null);
    const comum = {
      codigo: form.codigo.trim().toUpperCase(),
      descricao: form.descricao.trim() || null,
      tipoDesconto: form.tipo,
      valorDesconto: Number(form.valor),
      valorMinimoPedido: form.valorMinimo ? Number(form.valorMinimo) : 0,
      limiteTotalUsos: form.limite ? Number(form.limite) : null,
      usoUnicoPorCliente: form.usoUnico,
      dataInicio: deInputLocal(form.dataInicio),
      dataFim: deInputLocal(form.dataFim),
      // Escopo só viaja para quem pode usá-lo. Para os demais o backend mantém
      // LOJA — nem o campo existe na tela.
      ...(podeRede
        ? form.escopo === 'REDE'
          ? {
              escopo: 'REDE' as const,
              idRedeCardapio: form.idRede === '' ? null : form.idRede,
              limiteUsosPorLoja: form.limitePorLoja ? Number(form.limitePorLoja) : null,
            }
          : { escopo: 'LOJA' as const, idRedeCardapio: null, limiteUsosPorLoja: null }
        : {}),
    };

    try {
      if (ehEdicao) {
        await atualizar({ id: editandoId!, dados: { ...comum, ativo: form.ativo } }).unwrap();
      } else {
        await criar({
          // Cupom de rede não tem loja emissora: pertence à rede. A matriz da
          // franquia normalmente nem tem loja no cardápio.
          idConfiguracaoCardapio: podeRede && form.escopo === 'REDE' ? null : idConfig,
          ...comum,
        }).unwrap();
      }
      setEditandoId(null);
      mostrarSucesso(ehEdicao ? 'Cupom atualizado' : 'Cupom criado');
    } catch (err) {
      setErro((err as { data?: { mensagem?: string } })?.data?.mensagem ?? 'Falha ao salvar');
    }
  }

  const datasInvertidas =
    !!form.dataInicio && !!form.dataFim && new Date(form.dataFim) <= new Date(form.dataInicio);
  const freteAcimaDe100 = form.tipo === 'FRETE_PERCENTUAL' && Number(form.valor) > 100
  const podeSalvar =
    !!form.codigo.trim() &&
    (form.tipo === 'FRETE_GRATIS' || Number(form.valor) > 0) &&
    !freteAcimaDe100 &&
    !(form.escopo === 'REDE' && form.idRede === '') &&
    !datasInvertidas &&
    !salvando;

  const colunas = veRede ? 8 : 7;

  return (
    <>
      <HeaderTela
        titulo="Cupons de desconto"
        descricao="Crie códigos promocionais com desconto percentual, valor fixo ou desconto no frete, com limite de usos opcional."
        acoes={
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={abrirNovo}
            fullWidth
            sx={{ width: { xs: '100%', sm: 'auto' } }}
          >
            Novo cupom
          </Button>
        }
      />

      <Paper variant="outlined" sx={{ overflowX: 'auto' }}>
        {isLoading ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <CircularProgress />
          </Box>
        ) : (
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Código</TableCell>
                <TableCell>Descrição</TableCell>
                {veRede && <TableCell>Onde vale</TableCell>}
                <TableCell>Tipo</TableCell>
                <TableCell align="right">Valor</TableCell>
                <TableCell align="right">Min. pedido</TableCell>
                <TableCell align="right">Usos</TableCell>
                <TableCell align="right">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {cupons.map((c) => (
                <TableRow key={c.id} sx={{ opacity: c.ativo ? 1 : 0.6 }}>
                  <TableCell>
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                      {/* O código é o que o gestor procura e dita ao cliente —
                          tem que ser o elemento mais legível da linha. */}
                      <Box
                        component="span"
                        sx={{
                          fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
                          fontSize: '1rem',
                          fontWeight: 700,
                          letterSpacing: '0.06em',
                          lineHeight: 1.4,
                          px: 1,
                          py: 0.375,
                          borderRadius: 1,
                          bgcolor: 'action.hover',
                          border: '1px solid',
                          borderColor: 'divider',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {c.codigo}
                      </Box>
                      {!c.ativo && <Chip size="small" label="Inativo" />}
                      {c.usoUnicoPorCliente && <Chip size="small" variant="outlined" label="1x por cliente" />}
                    </Stack>
                  </TableCell>
                  <TableCell>{c.descricao ?? '—'}</TableCell>
                  {veRede && (
                    <TableCell>
                      {c.escopo === 'REDE' ? (
                        <Chip size="small" color="primary" variant="outlined" label="Rede" />
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          Esta loja
                        </Typography>
                      )}
                    </TableCell>
                  )}
                  <TableCell>
                    <Chip size="small" label={ROTULO_DO_TIPO[c.tipoDesconto]} />
                  </TableCell>
                  <TableCell align="right">{c.tipoDesconto === 'FRETE_GRATIS' ? '—' : c.valorDesconto}</TableCell>
                  <TableCell align="right">{c.valorMinimoPedido}</TableCell>
                  <TableCell align="right">
                    {c.usosAtuais}
                    {c.limiteTotalUsos != null && ` / ${c.limiteTotalUsos}`}
                  </TableCell>
                  <TableCell align="right">
                    {c.somenteLeitura ? (
                      <Tooltip title="Cupom da rede — quem edita é a matriz">
                        <Typography variant="caption" color="text.secondary">
                          da rede
                        </Typography>
                      </Tooltip>
                    ) : (
                      <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                        <IconButton onClick={() => abrirEdicao(c)} title="Editar">
                          <EditOutlinedIcon />
                        </IconButton>
                        <IconButton
                          onClick={() => setInativando({ id: c.id, codigo: c.codigo })}
                          color="error"
                          title="Inativar"
                          disabled={!c.ativo}
                        >
                          <DeleteOutlineIcon />
                        </IconButton>
                      </Stack>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {cupons.length === 0 && (
                <TableRow>
                  <TableCell colSpan={colunas} align="center">
                    <Typography color="text.secondary" sx={{ py: 3 }}>
                      Nenhum cupom cadastrado.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}
        <PaginacaoTabela
          total={total}
          pagina={pagina}
          tamanhoPagina={tamanhoPagina}
          aoMudarPagina={setPagina}
          aoMudarTamanho={(t) => {
            setTamanhoPagina(t);
            setPagina(0);
          }}
        />
      </Paper>

      <Dialog open={editandoId != null} onClose={() => setEditandoId(null)} fullWidth maxWidth="sm">
        <DialogTitle>{ehEdicao ? `Editar cupom ${form.codigo}` : 'Novo cupom'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Código"
              value={form.codigo}
              onChange={(e) => set('codigo', e.target.value.toUpperCase())}
              fullWidth
              required
            />
            <TextField
              label="Descrição"
              value={form.descricao}
              onChange={(e) => set('descricao', e.target.value)}
              fullWidth
            />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <Stack spacing={1} sx={{ flex: 1 }}>
                <TextField
                  select
                  label="Tipo de desconto"
                  value={ehCupomDeFrete(form.tipo) ? 'FRETE' : form.tipo}
                  onChange={(e) => escolherTipo(e.target.value === 'FRETE' ? 'FRETE_GRATIS' : (e.target.value as FormCupom['tipo']))}
                  fullWidth
                >
                  <MenuItem value="PERCENTUAL">Percentual (%)</MenuItem>
                  <MenuItem value="VALOR_FIXO">Valor fixo (R$)</MenuItem>
                  <MenuItem value="FRETE">Frete</MenuItem>
                </TextField>
                {ehCupomDeFrete(form.tipo) && (
                  <RadioGroup
                    aria-label="Desconto no frete"
                    value={form.tipo}
                    onChange={(_e, tipo) => escolherTipo(tipo as FormCupom['tipo'])}
                  >
                    <FormControlLabel value="FRETE_GRATIS" control={<Radio size="small" />} label="Grátis" />
                    <FormControlLabel value="FRETE_PERCENTUAL" control={<Radio size="small" />} label="Desconto em %" />
                    <FormControlLabel value="FRETE_VALOR" control={<Radio size="small" />} label="Desconto em R$" />
                  </RadioGroup>
                )}
              </Stack>
              <TextField
                label="Valor do desconto"
                value={form.tipo === 'FRETE_GRATIS' ? '100' : form.valor}
                onChange={(e) => set('valor', e.target.value)}
                type="number"
                disabled={form.tipo === 'FRETE_GRATIS'}
                error={freteAcimaDe100}
                helperText={freteAcimaDe100 ? 'O desconto no frete vai até 100%' : undefined}
                InputProps={adornoDoValor(form.tipo)}
                sx={{ flex: 1 }}
              />
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label="Pedido mínimo (R$)"
                value={form.valorMinimo}
                onChange={(e) => set('valorMinimo', e.target.value)}
                type="number"
                sx={{ flex: 1 }}
              />
              <TextField
                label="Limite total de usos"
                value={form.limite}
                onChange={(e) => set('limite', e.target.value)}
                type="number"
                helperText="Vazio = ilimitado"
                sx={{ flex: 1 }}
              />
            </Stack>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label="Início da validade"
                value={form.dataInicio}
                onChange={(e) => set('dataInicio', e.target.value)}
                type="datetime-local"
                InputLabelProps={{ shrink: true }}
                helperText="Vazio = vale desde já"
                sx={{ flex: 1 }}
              />
              <TextField
                label="Fim da validade"
                value={form.dataFim}
                onChange={(e) => set('dataFim', e.target.value)}
                type="datetime-local"
                InputLabelProps={{ shrink: true }}
                error={datasInvertidas}
                helperText={datasInvertidas ? 'Deve ser depois do início' : 'Vazio = sem prazo'}
                sx={{ flex: 1 }}
              />
            </Stack>

            <FormControlLabel
              control={<Checkbox checked={form.usoUnico} onChange={(e) => set('usoUnico', e.target.checked)} />}
              label="Uma única vez por cliente"
            />
            {form.usoUnico && (
              <Alert severity="info">
                O cliente precisa estar logado para usar este cupom — é a conta dele que a regra controla.
                {form.escopo === 'REDE' && ' Vale uma vez em toda a rede, não uma vez por loja.'}
              </Alert>
            )}

            {podeRede && (
              <>
                <TextField
                  select
                  label="Onde o cupom vale"
                  value={form.escopo}
                  onChange={(e) => set('escopo', e.target.value as FormCupom['escopo'])}
                  fullWidth
                >
                  <MenuItem value="LOJA">Somente esta loja</MenuItem>
                  <MenuItem value="REDE">Todas as lojas da rede</MenuItem>
                </TextField>
                {form.escopo === 'REDE' && (
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                    <TextField
                      select
                      label="Rede"
                      value={form.idRede}
                      onChange={(e) => set('idRede', Number(e.target.value))}
                      sx={{ flex: 1 }}
                      helperText={(redes ?? []).length === 0 ? 'Crie uma rede em "Rede de lojas"' : ' '}
                    >
                      {(redes ?? []).map((r) => (
                        <MenuItem key={r.id} value={r.id}>
                          {r.nome}
                        </MenuItem>
                      ))}
                    </TextField>
                    <TextField
                      label="Limite de usos por loja"
                      value={form.limitePorLoja}
                      onChange={(e) => set('limitePorLoja', e.target.value)}
                      type="number"
                      helperText="Vazio = sem teto por loja"
                      sx={{ flex: 1 }}
                    />
                  </Stack>
                )}
              </>
            )}

            {ehEdicao && (
              <FormControlLabel
                control={<Checkbox checked={form.ativo} onChange={(e) => set('ativo', e.target.checked)} />}
                label="Cupom ativo"
              />
            )}

            {erro && <Alert severity="error">{erro}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditandoId(null)}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={salvar}
            disabled={!podeSalvar}
            startIcon={salvando ? <CircularProgress size={18} color="inherit" /> : undefined}
          >
            {ehEdicao ? 'Salvar' : 'Criar'}
          </Button>
        </DialogActions>
      </Dialog>

      <DialogConfirmar
        aberto={inativando !== null}
        titulo={`Inativar o cupom ${inativando?.codigo ?? ''}?`}
        descricao="Ele deixa de ser aceito no checkout. O histórico de quem já resgatou continua registrado."
        textoConfirmar="Inativar"
        aoCancelar={() => setInativando(null)}
        aoConfirmar={() => {
          if (inativando) {
            void remover(inativando.id)
              .unwrap()
              .then(() => mostrarSucesso('Cupom inativado'))
              .catch(() => mostrarErro('Não foi possível inativar o cupom. Tente de novo.'));
          }
          setInativando(null);
        }}
      />

      <SnackbarFeedback feedback={feedback} aoFechar={fecharFeedback} />
    </>
  );
}
